import { FC, useState } from 'react'
import { Typography, Box, Fab, ListItemIcon, ListItemText, Tooltip, IconButton, Divider, FormControl, TextField, Switch, InputAdornment } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Menu as MenuIcon, ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import { StyledMenu, StyledMenuItem } from '../StyledMenu';
import { DownloadableFileVarButton, IDownloadbleFileVarButtonProps } from '../DownloadableFileButton/DownloadableFileButton';
import { FabLoader } from '../FabLoader';
import { truncateString } from 'TaxAnalyticsHelpers';
import { debounce } from 'lodash'

export interface DeloittePaperHeaderAction {
    label?: string;
    tooltip?: string;
    icon?: any;
    action?: any;
    checked?: boolean;
    type?: "switch" | "standard",
    disabled?: boolean;
}

interface DeloittePaperHeaderProps {
    label?: string;
    icon: any;
    subTitle?: string;
    actions?: (DeloittePaperHeaderAction | IDownloadbleFileVarButtonProps)[];
    loading?: boolean;
    backAction?: () => void;
    searchCallback?: (value: string) => void;
    searchLabel?: string;
    nomargin?: boolean;
}
const useStyles = makeStyles(theme => ({
    icon: {
        fontSize: '32px',
        marginRight: theme.spacing(2),
        width: '48px',
        heigth: '48px',
    },
    title: {
        fontSize: '24px',
        lineHeight: '32px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    subTitle: {
        fontSize: '16px',
        lineHeight: '24px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    container: {
        marginBottom: theme.spacing(4),
        height: '56px',
        flex: '0 1 auto',
    },
    containerNoMargin: {
        marginBottom: theme.spacing(0),
        height: '56px',
        flex: '0 1 auto',
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
    },
    fabProgress: {
        color: theme.deloitte.brand,
        position: 'absolute',
        top: -8,
        left: 0,
        zIndex: 1,
    },
    wrap: {
        marginRight: 8,
        minWidth: 0,
        flexGrow: 1
    }
}));

export const DeloittePaperHeader: FC<DeloittePaperHeaderProps> = (props) => {
    const { icon, label, subTitle, actions, loading, backAction, nomargin, searchCallback } = props;
    const Icon = icon;
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const debouncedSearch = searchCallback ? debounce(searchCallback!, 500) : (v: string) => { };

    return (
        <Box display="flex" alignItems="stretch" className={nomargin ? classes.containerNoMargin : classes.container}>
            <Box display="flex" alignItems="center" style={{ overflow: 'hidden', width: "100%" }}>
                {
                    backAction &&
                    <IconButton size="small" aria-label="backAction" onClick={() => backAction()}>
                        <ArrowBack />
                    </IconButton>
                }
                <FabLoader loading={loading ?? false} size={48} className={classes.fabProgress}>
                    <Icon className={classes.icon} />
                </FabLoader>

                <Box alignItems="center" className={classes.wrap}>
                    <Typography className={classes.title}>{truncateString(label, 80)}</Typography>
                    {subTitle &&
                        <Typography className={classes.subTitle}>{truncateString(subTitle, 80)}</Typography>
                    }
                </Box>
                {searchCallback &&
                    <Box flexGrow={0.5} alignItems="center" >
                        <TextField
                            id="searchBar"
                            onChange={(event) => { debouncedSearch(event.target.value) }}
                            fullWidth
                            variant="standard" label={props.searchLabel ?? "Recherche..."}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                }
                {actions && actions.length === 1 &&
                    <Box alignItems="center" style={{ lineHeight: '48px' }}>
                        {actions.map((action: any, index) => {
                            if (action.action) {
                                const ActionIcon = action.icon;
                                return (
                                    <Tooltip title={action.tooltip ?? action.label} key={index} >
                                        <span>
                                            <Fab disabled={loading || action.disabled} size="small" color="primary" aria-label={action.label} onClick={() => action.action()} style={{ boxShadow: 'none' }}>
                                                <ActionIcon />
                                            </Fab>
                                        </span>
                                    </Tooltip>
                                )
                            }
                            else {
                                return <DownloadableFileVarButton key={index} analysisId={action.analysisId} name={action.name} downloadFileName={action.downloadFileName} title={action.label} extension={action.extension} />
                            }
                        }
                        )}
                    </Box>
                }
                {actions && actions.length > 1 &&
                    <Box alignItems="center" style={{ lineHeight: '48px' }} >
                        <Fab size="small" color="primary"
                            aria-controls="customized-menu"
                            aria-haspopup="true"
                            onClick={handleClick}
                            disabled={loading}
                            style={{ boxShadow: 'none' }}
                        >
                            <MenuIcon />
                        </Fab>
                        <StyledMenu
                            id="actions-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            autoFocus={false}
                        >
                            {actions.map((action: DeloittePaperHeaderAction | IDownloadbleFileVarButtonProps, index) => {
                                if (action.label === "divider") {
                                    return <Divider variant="middle" component="li" key={index} />
                                }
                                if ("action" in action && action.action) {
                                    const ActionIcon = action.icon;
                                    if (action.type === "switch") {
                                        return (
                                            <StyledMenuItem key={index}>
                                                <ListItemIcon>
                                                    <ActionIcon />
                                                </ListItemIcon>
                                                <ListItemText id="switch-list-label-wifi" primary={action.label} />
                                                <Switch
                                                    edge="end"
                                                    onChange={() => {
                                                        action.action();
                                                    }}
                                                    checked={action.checked}
                                                    inputProps={{
                                                        'aria-labelledby': 'switch-list-label-wifi',
                                                    }}
                                                />
                                            </StyledMenuItem>
                                        )
                                    }
                                    return (
                                        <StyledMenuItem key={index} disabled={loading || action.disabled } onClick={() => { action.action(); handleClose(); }}>
                                            <ListItemIcon>
                                                <ActionIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={action.label} />
                                        </StyledMenuItem>
                                    )
                                }
                                if ("analysisId" in action) {
                                    return (
                                        <StyledMenuItem key={index}>
                                            <DownloadableFileVarButton key={index} analysisId={action.analysisId} name={action.name} downloadFileName={action.downloadFileName} title={action.label!} extension={action.extension} />
                                        </StyledMenuItem>
                                    )
                                }
                            })}

                        </StyledMenu>
                    </Box>
                }
            </Box>
        </Box >
    )
}